import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Formik } from 'formik';
import * as yup from 'yup';
import { MFTextField, MFSubmitButton } from '../../lib/formik';
import { useDispatch } from 'react-redux';
import { loginWithOtp } from '../../redux-store/actions/auth';
import { useHistory } from 'react-router';
import { emailRegex, nonDigitRegex, phoneRegExp } from '../../utils/regex';
import { LoginWithOtpResponseBody } from '../../redux-store/types/api-types';
import { CountryCodesDropDown, LoginLayout } from '../commonComponents';
import {
  allowOnlyNumbers,
  getCountryCodes,
  preventSpecialCharacters,
} from '../../utils/utilityFunctions';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { useEffect, useState } from 'react';
import { EMAIL_VALIDATION_MESSAGE, PHONE_VALIDATION_MESSAGE } from '../../utils/constant';
import { RequiredFieldsMessage } from '../RequiredFieldsMessage';

interface Values {
  mobile: string;
  countryCode: string;
  email: string;
  countryNameAndCode: string;
}

const validationSchema = yup.object().shape({
  // mobile: yup
  //   .string()
  //   .required('Mobile number is required')
  //   .matches(phoneRegExp, 'Mobile number is not valid')
  //   .length(10, 'Mobile number should be maximum 10 digits'),
  mobile: yup.string().test('mobile', (value, context) => {
    const { createError, path, parent } = context;
    const codesList = getCountryCodes()
      .map((codes) => {
        if (codes.label === parent.countryNameAndCode) {
          return codes.countryCode;
        }
      })
      .filter((ele) => ele)
      .toString();
    if (!value) {
      return createError({
        message: 'Mobile number is required',
        path,
      });
    }
    if (parent.countryNameAndCode === 'India: +91' && !phoneRegExp.test(value)) {
      return createError({
        message: PHONE_VALIDATION_MESSAGE,
        path,
      });
    }
    if (!isValidPhoneNumber(value, codesList as CountryCode)) {
      return createError({
        message: PHONE_VALIDATION_MESSAGE,
        path,
      });
    }
    return true;
  }),
  countryNameAndCode: yup
    .string()
    .nullable()
    .test('countryNameAndCode', (value, context) => {
      const { createError, path, parent } = context;
      const codesList = getCountryCodes()
        .map((codes) => {
          if (codes.label === value) {
            return codes.countryCode;
          }
        })
        .filter((ele) => ele)
        .toString();
      if (!value) {
        return createError({
          message: 'Country Code is required',
          path,
        });
      }
      if (parent.mobile && !isValidPhoneNumber(parent.mobile, codesList as CountryCode)) {
        return createError({
          message: PHONE_VALIDATION_MESSAGE,
          path,
        });
      }

      return true;
    }),
  email: yup.string().test('email', (value, context) => {
    const { createError, path } = context;
    if (!value) {
      return createError({
        message: 'Email ID is required',
        path,
      });
    }
    if (!emailRegex.test(value)) {
      return createError({
        message: EMAIL_VALIDATION_MESSAGE,
        path,
      });
    }
    return true;
  }),
});

export default function MobileLogin(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const onSubmit = async (values: Values) => {
    try {
      const {
        mobile = '',
        countryCode = '+91',
        countryNameAndCode = 'India: +91',
        email = '',
      } = values;
      const res = (await dispatch(
        loginWithOtp({
          phone: mobile.trim(),
          countryCode: countryNameAndCode?.split(':')[1].trim(),
          email: email,
        })
      )) as unknown as LoginWithOtpResponseBody;
      history.push('verify-otp', {
        mobile,
        countryCode: countryNameAndCode?.split(':')[1].trim(),
        emailDecrypted: email,
        ...(res || {}),
      });
    } catch (e) {
      console.error((e as unknown as Error).message);
    }
  };
  const initialValues: Values = {
    mobile: '',
    countryCode: '+91',
    countryNameAndCode: 'India: +91',
    email: '',
  };
  const [mobileElementWidth, setMobileElementWidth] = useState(null);
  useEffect(() => {
    const mobileElement = document.getElementsByName('mobile');
    setMobileElementWidth(mobileElement[0]?.parentElement?.clientWidth as any);
  }, []);
  return (
    <LoginLayout LoginText="Login">
      <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema}>
        {({ handleSubmit, values }) => (
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{
              '.MuiLoadingButton-root': {
                mt: 5,
              },
              '.MuiInputLabel-root': {
                fontFamily: 'Poppins, sans-serif',
                fontSize: 16,
                fontWeight: 500,
              },
            }}>
            <RequiredFieldsMessage />
            <MFTextField
              id="mobileNumber"
              label="Mobile Number *"
              name="mobile"
              autoComplete="tel"
              placeholder="Enter Mobile number"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              sx={{
                '&:has(input:focus-visible)': {
                  outline: '1px solid #1976d2',
                },
              }}
              enableStatusMessage
              startAdornment={
                <CountryCodesDropDown
                  ElementWidth={mobileElementWidth && mobileElementWidth}
                  name={'countryNameAndCode'}
                  value={values.countryNameAndCode}
                />
              }
              regexForFilterValue={nonDigitRegex}
              aria-label="enter mobile number"
            />
            <MFTextField
              autoComplete="email"
              // autoFocus
              label="Email ID "
              name="email"
              placeholder="Enter Email ID"
              required
              aria-label="Enter Email ID"
              sx={{
                '&:has(input:focus-visible)': {
                  outline: '1px solid #1976d2',
                },
              }}
            />
            {/* <MFTextField
              required
              label="Enter Mobile Number"
              name="mobile"
              type="text"
              placeholder="Enter Mobile Number"
              autoComplete="off"
              autoFocus
            /> */}
            <MFSubmitButton label="Get OTP" aria-label="Get Login OTP" />
          </Box>
        )}
      </Formik>
    </LoginLayout>
  );
}
